import React, { useState } from 'react';
import { useAllUsers, useAllBusinesses } from '@/hooks/admin/useAdminData';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Loader2, Building2, Users, ChevronDown, ChevronRight, MoreHorizontal, Trash2, CreditCard, KeyRound, UserCog, Settings2, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/ui/use-toast';

const SUBSCRIPTION_PLANS = [
  { id: 'ab9d01c3-f433-445e-bb21-dfa7bb89dfba', name: 'free', display_name: 'Free Plan' },
  { id: 'cdf29039-d0b4-4446-b25e-647b0fd8823e', name: 'pro', display_name: 'Pro Plan' },
  { id: '8a72df5e-a8ec-4846-a3bd-024dc2ccd46d', name: 'enterprise', display_name: 'Enterprise Plan' },
];

const getStatusBadgeVariant = (status: string | null) => {
  switch (status) {
    case 'active':
      return 'default';
    case 'trialing':
      return 'secondary';
    case 'past_due':
      return 'destructive';
    case 'canceled':
      return 'outline';
    default:
      return 'outline';
  }
};

const UsersManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: users, isLoading: usersLoading, error: usersError } = useAllUsers();
  const { data: businesses, isLoading: businessesLoading } = useAllBusinesses();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  // Dialog states
  const [deleteUserDialog, setDeleteUserDialog] = useState<{ open: boolean; user: { id: string; email: string } | null }>({ open: false, user: null });
  const [deleteBusinessDialog, setDeleteBusinessDialog] = useState<{ open: boolean; business: { id: string; name: string } | null }>({ open: false, business: null });
  const [assignPlanDialog, setAssignPlanDialog] = useState<{ open: boolean; user: { id: string; email: string; currentPlan: string | null } | null }>({ open: false, user: null });
  const [setLimitsDialog, setSetLimitsDialog] = useState<{ open: boolean; user: { id: string; email: string; maxBusinesses: number | null; maxEmployees: number | null } | null }>({ open: false, user: null });
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [limitsForm, setLimitsForm] = useState<{ maxBusinesses: string; maxEmployees: string }>({ maxBusinesses: '', maxEmployees: '' });
  const [isProcessing, setIsProcessing] = useState(false);

  const isLoading = usersLoading || businessesLoading;

  // Group businesses by user_id
  const businessesByUser = React.useMemo(() => {
    const map = new Map<string, typeof businesses>();
    businesses?.forEach((biz) => {
      const existing = map.get(biz.user_id) || [];
      map.set(biz.user_id, [...existing, biz]);
    });
    return map;
  }, [businesses]);

  const filteredUsers = users?.filter((user) =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.plan_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    businessesByUser.get(user.id)?.some(b =>
      b.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const toggleUserExpanded = (userId: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleDeleteUser = async () => {
    if (!deleteUserDialog.user) return;

    setIsProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke('admin-delete-user', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: { userId: deleteUserDialog.user.id },
      });

      if (error) throw error;

      toast.success('User deleted', { description: `${deleteUserDialog.user.email} and all related data has been deleted.` });
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setDeleteUserDialog({ open: false, user: null });
    } catch (err) {
      toast.error('Failed to delete user', { description: (err as Error).message });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteBusiness = async () => {
    if (!deleteBusinessDialog.business) return;

    setIsProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke('admin-delete-business', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: { businessId: deleteBusinessDialog.business.id },
      });

      if (error) throw error;

      toast.success('Business deleted', { description: `${deleteBusinessDialog.business.name} and all related data has been deleted.` });
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setDeleteBusinessDialog({ open: false, business: null });
    } catch (err) {
      toast.error('Failed to delete business', { description: (err as Error).message });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAssignPlan = async () => {
    if (!assignPlanDialog.user || !selectedPlan) return;

    setIsProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke('admin-assign-plan', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: { userId: assignPlanDialog.user.id, planId: selectedPlan },
      });

      if (error) throw error;

      const planName = SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.display_name;
      toast.success('Plan assigned', { description: `${assignPlanDialog.user.email} now has ${planName}.` });
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setAssignPlanDialog({ open: false, user: null });
      setSelectedPlan('');
    } catch (err) {
      toast.error('Failed to assign plan', { description: (err as Error).message });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendPasswordReset = async (email: string) => {
    setIsProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke('admin-send-password-reset', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: { email },
      });

      if (error) throw error;

      toast.success('Password reset sent', { description: `Reset email sent to ${email}` });
    } catch (err) {
      toast.error('Failed to send reset email', { description: (err as Error).message });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImpersonateUser = async (email: string) => {
    setIsProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await supabase.functions.invoke('admin-impersonate-user', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: { email },
      });

      if (response.error) throw response.error;

      const link = response.data?.link;
      if (link) {
        // Open magic link in new tab
        window.open(link, '_blank');
        toast.success('Impersonation link opened', { description: `Logged in as ${email} in new tab` });
      } else {
        throw new Error('No magic link returned');
      }
    } catch (err) {
      toast.error('Failed to impersonate user', { description: (err as Error).message });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSetLimits = async () => {
    if (!setLimitsDialog.user) return;

    setIsProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke('admin-set-user-limits', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: {
          userId: setLimitsDialog.user.id,
          maxBusinesses: limitsForm.maxBusinesses,
          maxEmployeesPerBusiness: limitsForm.maxEmployees,
        },
      });

      if (error) throw error;

      toast.success('Limits updated', { description: `Custom limits set for ${setLimitsDialog.user.email}` });
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setSetLimitsDialog({ open: false, user: null });
      setLimitsForm({ maxBusinesses: '', maxEmployees: '' });
    } catch (err) {
      toast.error('Failed to set limits', { description: (err as Error).message });
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (usersError) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Failed to load users</p>
        <p className="text-sm text-muted-foreground mt-2">{(usersError as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Users & Businesses</h1>
        <p className="text-muted-foreground">
          {users?.length || 0} users · {businesses?.length || 0} businesses
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Users</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users or businesses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredUsers?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? 'No matching users found' : 'No users yet'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Businesses</TableHead>
                  <TableHead>Limits</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers?.map((user) => {
                  const userBusinesses = businessesByUser.get(user.id) || [];
                  const isExpanded = expandedUsers.has(user.id);
                  const hasBusinesses = userBusinesses.length > 0;

                  return (
                    <React.Fragment key={user.id}>
                      <TableRow className={hasBusinesses ? 'cursor-pointer hover:bg-muted/50' : ''}>
                        <TableCell className="w-8" onClick={() => hasBusinesses && toggleUserExpanded(user.id)}>
                          {hasBusinesses && (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="font-medium" onClick={() => hasBusinesses && toggleUserExpanded(user.id)}>{user.email}</TableCell>
                        <TableCell onClick={() => hasBusinesses && toggleUserExpanded(user.id)}>
                          {user.plan_display_name || user.plan_name ? (
                            <Badge>
                              {user.plan_display_name || user.plan_name}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">No plan</span>
                          )}
                        </TableCell>
                        <TableCell onClick={() => hasBusinesses && toggleUserExpanded(user.id)}>
                          {user.subscription_status ? (
                            <Badge variant={getStatusBadgeVariant(user.subscription_status)}>
                              {user.subscription_status}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell onClick={() => hasBusinesses && toggleUserExpanded(user.id)}>
                          <div className="flex items-center gap-1">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span>
                              {user.business_count}/{user.max_businesses ?? '∞'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground" onClick={() => hasBusinesses && toggleUserExpanded(user.id)}>
                          {user.max_employees_per_business ? (
                            <span>{user.max_employees_per_business} emp/biz</span>
                          ) : (
                            <span>∞ emp/biz</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground" onClick={() => hasBusinesses && toggleUserExpanded(user.id)}>
                          {format(new Date(user.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleImpersonateUser(user.email)} disabled={isProcessing}>
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Impersonate User
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleSendPasswordReset(user.email)} disabled={isProcessing}>
                                <KeyRound className="h-4 w-4 mr-2" />
                                Send Password Reset
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => {
                                setAssignPlanDialog({ open: true, user: { id: user.id, email: user.email, currentPlan: user.plan_name } });
                                setSelectedPlan(SUBSCRIPTION_PLANS.find(p => p.name === user.plan_name)?.id || '');
                              }}>
                                <CreditCard className="h-4 w-4 mr-2" />
                                Assign Plan
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setSetLimitsDialog({ open: true, user: { id: user.id, email: user.email, maxBusinesses: user.max_businesses, maxEmployees: user.max_employees_per_business } });
                                setLimitsForm({
                                  maxBusinesses: user.max_businesses?.toString() || '',
                                  maxEmployees: user.max_employees_per_business?.toString() || ''
                                });
                              }}>
                                <Settings2 className="h-4 w-4 mr-2" />
                                Set Limits
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeleteUserDialog({ open: true, user: { id: user.id, email: user.email } })}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>

                      {/* Expanded business rows */}
                      {isExpanded && userBusinesses.map((biz) => (
                        <TableRow key={biz.id} className="bg-muted/30">
                          <TableCell></TableCell>
                          <TableCell colSpan={2} className="pl-8">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-primary" />
                              <span className="font-medium">{biz.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm">
                              <Users className="h-3 w-3 text-muted-foreground" />
                              <span>{biz.employee_count} employees</span>
                            </div>
                          </TableCell>
                          <TableCell colSpan={2} className="text-sm text-muted-foreground">
                            Created {format(new Date(biz.created_at), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              onClick={() => setDeleteBusinessDialog({ open: true, business: { id: biz.id, name: biz.name } })}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete User Dialog */}
      <Dialog open={deleteUserDialog.open} onOpenChange={(open) => setDeleteUserDialog({ open, user: open ? deleteUserDialog.user : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteUserDialog.user?.email}</strong>?
              This will permanently delete the user and all their data including businesses, employees, shifts, reviews, and more. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUserDialog({ open: false, user: null })} disabled={isProcessing}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={isProcessing}>
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Business Dialog */}
      <Dialog open={deleteBusinessDialog.open} onOpenChange={(open) => setDeleteBusinessDialog({ open, business: open ? deleteBusinessDialog.business : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Business</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteBusinessDialog.business?.name}</strong>?
              This will permanently delete the business and all related data including employees, shifts, reviews, and more. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteBusinessDialog({ open: false, business: null })} disabled={isProcessing}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteBusiness} disabled={isProcessing}>
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete Business
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Plan Dialog */}
      <Dialog open={assignPlanDialog.open} onOpenChange={(open) => { setAssignPlanDialog({ open, user: open ? assignPlanDialog.user : null }); if (!open) setSelectedPlan(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Subscription Plan</DialogTitle>
            <DialogDescription>
              Choose a subscription plan for <strong>{assignPlanDialog.user?.email}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedPlan} onValueChange={setSelectedPlan}>
              <SelectTrigger>
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent>
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssignPlanDialog({ open: false, user: null }); setSelectedPlan(''); }} disabled={isProcessing}>
              Cancel
            </Button>
            <Button onClick={handleAssignPlan} disabled={isProcessing || !selectedPlan}>
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Assign Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Limits Dialog */}
      <Dialog open={setLimitsDialog.open} onOpenChange={(open) => { setSetLimitsDialog({ open, user: open ? setLimitsDialog.user : null }); if (!open) setLimitsForm({ maxBusinesses: '', maxEmployees: '' }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Custom Limits</DialogTitle>
            <DialogDescription>
              Set custom limits for <strong>{setLimitsDialog.user?.email}</strong>. Leave empty to use plan defaults.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Max Businesses</label>
              <Input
                type="number"
                min="1"
                placeholder="Plan default"
                value={limitsForm.maxBusinesses}
                onChange={(e) => setLimitsForm(prev => ({ ...prev, maxBusinesses: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Current plan default: {setLimitsDialog.user?.maxBusinesses ?? 'Unlimited'}
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Max Employees per Business</label>
              <Input
                type="number"
                min="1"
                placeholder="Plan default"
                value={limitsForm.maxEmployees}
                onChange={(e) => setLimitsForm(prev => ({ ...prev, maxEmployees: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Current plan default: {setLimitsDialog.user?.maxEmployees ?? 'Unlimited'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSetLimitsDialog({ open: false, user: null }); setLimitsForm({ maxBusinesses: '', maxEmployees: '' }); }} disabled={isProcessing}>
              Cancel
            </Button>
            <Button onClick={handleSetLimits} disabled={isProcessing}>
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Limits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagement;
